import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReportCol, ReportDef } from '@/components/reports/reportDefs'

const PREFIX = 'reports.cols.'

/**
 * Columnas visibles de un report, con la elección del usuario persistida.
 *
 * Por defecto se ven las del cliente y las nuestras de control, y se ocultan las
 * técnicas: las técnicas están para diagnosticar, no para el informe. Solo se
 * guardan las excepciones a ese reparto, de modo que añadir o quitar una columna
 * del registro no deja basura ni resucita columnas que ya no existen.
 */
export function useVisibleReportCols(def: ReportDef) {
  const storageKey = PREFIX + def.storageKey

  const [overrides, setOverrides] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return {}
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const known = new Set(def.cols.map(c => c.key))
      const out: Record<string, boolean> = {}
      for (const [k, v] of Object.entries(parsed)) {
        if (known.has(k) && typeof v === 'boolean') out[k] = v
      }
      return out
    } catch {
      return {}
    }
  })

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(overrides)) } catch { /* ignore */ }
  }, [storageKey, overrides])

  /** Columnas que pueden verse en pantalla: las de solo Excel no cuentan. */
  const dePantalla = useMemo(() => def.cols.filter(c => !c.excelOnly), [def])

  const visible = useMemo(() => {
    const set = new Set<string>()
    for (const c of dePantalla) {
      const on = overrides[c.key] ?? c.tier !== 'technical'
      if (on) set.add(c.key)
    }
    return set
  }, [dePantalla, overrides])

  const toggle = useCallback((key: string) => {
    setOverrides(prev => {
      const col = def.cols.find(c => c.key === key)
      if (!col) return prev
      const current = prev[key] ?? col.tier !== 'technical'
      return { ...prev, [key]: !current }
    })
  }, [def])

  const reset = useCallback(() => setOverrides({}), [])

  /** Columnas visibles en pantalla, en el orden del registro. */
  const cols: ReportCol[] = useMemo(
    () => dePantalla.filter(c => visible.has(c.key)),
    [dePantalla, visible],
  )

  /**
   * Columnas del Excel: las visibles más las de solo Excel, en orden de registro.
   * El Excel exporta lo que hay en pantalla y, además, lo que el informe del
   * cliente necesita aunque no se muestre.
   */
  const excelCols: ReportCol[] = useMemo(
    () => def.cols.filter(c => c.excelOnly || visible.has(c.key)),
    [def, visible],
  )

  /** Lo que ofrece el menú de columnas. */
  const menuItems = useMemo(() => dePantalla.map(c => c.key), [dePantalla])

  return { visible, cols, excelCols, menuItems, toggle, reset }
}
